import { useState, useEffect, useCallback } from 'react';
import { apiService, Tutorial } from '../services/api';

// Generic hook for API data fetching
export function useApiData<T>(
  fetchFunction: () => Promise<T>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('API fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Hook for products
export function useProducts() {
  return useApiData(
    () => apiService.getProducts(),
    []
  );
}

// Hook for a single product
export function useProduct(id: string) {
  return useApiData(
    () => apiService.getProduct(id),
    [id]
  );
}

// Hook for products by category
export function useProductsByCategory(category: string) {
  return useApiData(
    () => apiService.getProductsByCategory(category),
    [category]
  );
}

// Hook for tutorials with pagination
export function useTutorials(page: number = 1, limit: number = 10, language: string = 'en') {
  return useApiData(
    () => apiService.getTutorials(page, limit, language),
    [page, limit, language]
  );
}

// Hook for all tutorials - fetches all by making multiple paginated requests
export function useAllTutorials(language: string = 'all') {
  const [allTutorials, setAllTutorials] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    const fetchAllTutorials = async () => {
      setLoading(true);
      setError(null);
      const tutorials: Tutorial[] = [];
      let page = 1;
      const limit = 100; // Maximum allowed by backend
      let hasMore = true;
      let total = 0;

      console.log('[useAllTutorials] Starting to fetch all tutorials with limit=100 per page');
      
      try {
        while (hasMore) {
          console.log(`[useAllTutorials] Fetching page ${page} with limit=${limit}`);
          const response = await apiService.getTutorials(page, limit, language);
          if (response.tutorials && response.tutorials.length > 0) {
            tutorials.push(...response.tutorials);
            console.log(`[useAllTutorials] Page ${page}: Got ${response.tutorials.length} tutorials, total so far: ${tutorials.length}`);
            if (response.pagination) {
              total = response.pagination.total || 0;
              setPagination({ ...response.pagination, total });
              // Check if we've fetched all tutorials
              if (tutorials.length >= total || response.tutorials.length < limit) {
                console.log(`[useAllTutorials] All tutorials fetched. Total: ${tutorials.length}`);
                hasMore = false;
              } else {
                page++;
              }
            } else {
              // If no pagination info, stop after first page
              console.log('[useAllTutorials] No pagination info, stopping after first page');
              hasMore = false;
            }
          } else {
            console.log(`[useAllTutorials] Page ${page}: No tutorials returned, stopping`);
            hasMore = false;
          }
        }
        setAllTutorials(tutorials);
        console.log(`[useAllTutorials] Completed. Total tutorials: ${tutorials.length}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tutorials');
        console.error('[useAllTutorials] Error fetching all tutorials:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllTutorials();
  }, [language, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  return {
    data: { tutorials: allTutorials, pagination },
    loading,
    error,
    refetch
  };
}

// Hook for a single tutorial
export function useTutorial(id: string) {
  return useApiData(
    () => apiService.getTutorial(id),
    [id]
  );
}

// Hook for tutorials by category
export function useTutorialsByCategory(category: string) {
  return useApiData(
    () => apiService.getTutorialsByCategory(category),
    [category]
  );
}

// Hook for search functionality
export function useSearch(query: string, type?: 'product' | 'tutorial' | 'user' | 'category' | 'glossary') {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use backend search endpoint
      const response = await apiService.search(query, 50);
      
      // Backend returns { results, total, tutorialCount, ... } directly
      // The request method returns the JSON data directly (not wrapped in ApiResponse)
      // So response is { results: [...], total: ..., ... }
      const resultsArray = (response as any).results || (response as any).data?.results || [];
      
      if (Array.isArray(resultsArray) && resultsArray.length > 0) {
        let filteredResults = resultsArray;
        
        // Filter by type if specified
        if (type) {
          filteredResults = filteredResults.filter((item: any) => item.type === type);
        }
        
        // Transform results to match expected format
        const transformedResults = filteredResults.map((result: any) => ({
          id: result.id,
          title: result.title || result.term || '',
          description: result.content || result.description || result.definition || '',
          type: result.type,
          url: result.url,
          category: result.category,
          difficulty: result.difficulty,
          relevanceScore: result.relevanceScore,
          term: result.term,
          definition: result.definition
        }));
        
        setResults(transformedResults);
      } else {
        setResults([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, type]);

  useEffect(() => {
    const timeoutId = setTimeout(search, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [search]);

  return { results, loading, error };
}
